import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Autocomplete } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import BootstrapTextField from "Common/BootstrapTextField";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import FormattedDateTime from "components/FormattedDateTime/FormattedDateTime";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import FilePreviewModal from "pages/Warehouse/ItemInward2/GRNDetails/FilePreviewModal/FilePreviewModal";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { fetchStockTransferById } from "slices/Inventory/Dispatch/StockTransfer/thunk";
import { resetGRNState } from "slices/Warehouse/ItemInward2/GRN/reducer";
import { createGRN } from "slices/Warehouse/ItemInward2/GRN/thunk";
import Swal from "sweetalert2";
import * as Yup from "yup";
import ViewBagsModal from "./ViewBagsModal";

type StockTransferRouteParams = {
  id: string;
};

const StockTransferDetails: React.FC = () => {
  const { id } = useParams<StockTransferRouteParams>();

  const selectStockTransfer = createSelector(
    (state: any) => state.InventoryStockTransferDispatch,
    (item) => ({
      details: item.details,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const { details, loading, error } = useSelector(selectStockTransfer);
  const dispatch_items = details?.dispatch_items;

  useEffect(() => {
    if (id) {
      dispatch(fetchStockTransferById(id));
    }
  }, [id]);

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showBagsModal, setShowBagsModal] = useState<boolean>(false);
  const [selectedItemBags, setSelectedItemBags] = useState<any[]>([]);
  const [selectedItemName, setSelectedItemName] = useState<string>("");

  return (
    <>
      <PageContainer>
        <FormHeader
          title={"View Details"}
          firstLabel="Stock Transfer"
          secondLabel="View Details"
          handleArrowClick={() => navigate("/warehouse-dispatch")}
        />

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div>
            <hr />
            <Row
              className="mb-2"
              style={{ marginLeft: "3px", marginTop: "2px" }}
            >
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch ID :</strong>{" "}
                  {details?.dispatch_number || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch Date :</strong>{" "}
                  <FormattedDateTime dateString={details?.dispatch_date} format="date-only" />
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch By :</strong>{" "}
                  {details?.created_by_user?.username || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Challan No. :</strong> {details?.challan_no || "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                    <strong>Document :</strong>{" "}
                    {details?.document ? (
                      <button
                        type="button"
                        onClick={() => setShowPreview(true)}
                        style={{
                          color: "blue",
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0,
                          textDecoration: "underline",
                        }}
                      >
                        View
                      </button>
                    ) : (
                      "-"
                    )}
                  </p>
              </Col>

              {/* Document Viewer Popup */}
              <FilePreviewModal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                url={details?.document}
              />

              {/* Bag Details Modal */}
              <ViewBagsModal
                show={showBagsModal}
                handleClose={() => {
                  setShowBagsModal(false);
                  setSelectedItemBags([]);
                  setSelectedItemName("");
                }}
                bags={selectedItemBags}
                itemName={selectedItemName}
              />
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Issue From :</strong>{" "}
                  {details?.transfer_from_warehouse?.warehouse_name || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Transfer To :</strong>{" "}
                  {details?.transfer_to_warehouse?.warehouse_name || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Remarks :</strong> {details?.remark || "-"}
                </p>
              </Col>
            </Row>

            <hr />

            <div className="mb-3">
              <h6
                className="mb-3"
                style={{ fontWeight: "600", color: "#212529" }}
              >
                Item Details
              </h6>
              <div className="mb-2">
                <Table hover responsive className="align-middle text-center">
                  <thead>
                    <tr>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "80px" }}
                      >
                        Sr No.
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Items
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Item Code
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Sub Category
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Unit
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Bag No.
                      </th>

                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Dispatch Quantity
                      </th>
                      <th
                        style={{ ...tableStyles.headerAlt, minWidth: "150px" }}
                      >
                        Remarks
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {dispatch_items?.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          style={{ ...tableStyles.cell, textAlign: "center" }}
                          className="py-3"
                        >
                          No Items added.
                        </td>
                      </tr>
                    ) : (
                      dispatch_items?.map((row: any, idx: number) => {
                        return (
                          <tr key={idx}>
                            <td style={tableStyles.cell}>{idx + 1}</td>

                            {/* Items */}
                            <td style={tableStyles.cell}>
                              {row?.item_name || "-"}
                            </td>

                            {/* Item Code */}
                            <td style={tableStyles.cell}>
                              {row?.item_code || "-"}
                            </td>

                            {/* Category */}
                            <td style={tableStyles.cell}>
                              {row?.category_name || "-"}
                            </td>

                            {/* Sub Category */}
                            <td style={tableStyles.cell}>
                              {row?.sub_category_name || "-"}
                            </td>

                            {/* Unit */}
                            <td style={tableStyles.cell}>
                              {row?.unit_name || row?.unit || "-"}
                            </td>

                            {/* Bag No. */}
                            <td style={tableStyles.cell}>
                              {row?.bags && row.bags.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedItemBags(row.bags || []);
                                    setSelectedItemName(row?.item_name || "");
                                    setShowBagsModal(true);
                                  }}
                                  style={{
                                    color: "blue",
                                    cursor: "pointer",
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    textDecoration: "underline",
                                  }}
                                >
                                  View Bags ({row.bags.length})
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>

                            {/* Dispatch Quantity */}
                            <td style={tableStyles.cell}>
                              {row?.dispatch_quantity || "-"}
                            </td>
                            {/* Remarks */}
                            <td className="remark-cell" style={tableStyles.cell}>
                              <TruncatedTextCell
                                text={row?.remarks || row?.remark}
                                maxWidth="300px"
                                maxLines={4}
                                rowIndex={idx}
                                fallbackText="-"
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton
                label="Back"
                onClick={() => {
                  navigate("/inventory-dispatch");
                }}
              />
            </div>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default StockTransferDetails;
